import { useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import {
  getLocationCellDisplay,
  getLocationTooltipRows,
  parseLocationPath,
} from "../../../helpers/locationDisplayUtils";
import { useLocationDisplayMode } from "./LocationDisplayModeContext";
import LocationHierarchyTooltip from "./LocationHierarchyTooltip";

interface LocationTableCellProps {
  locationFullPath?: string | null;
  className?: string;
}

const EMPTY_PLACEHOLDER = "—";

const LocationTableCell = ({
  locationFullPath,
  className,
}: LocationTableCellProps) => {
  const { t } = useTranslation();
  const { activeMode, containerRef } = useLocationDisplayMode();
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<DOMRect | null>(null);

  const tiers = parseLocationPath(locationFullPath);
  const display = getLocationCellDisplay(tiers, activeMode);
  const tooltipRows = getLocationTooltipRows(tiers, t);

  const handleMouseEnter = () => {
    if (!tooltipRows.length || !cellRef.current) {
      return;
    }
    setTooltipAnchor(cellRef.current.getBoundingClientRect());
  };

  const handleMouseLeave = () => {
    setTooltipAnchor(null);
  };

  const closeTooltip = useCallback(() => setTooltipAnchor(null), []);

  const containerRect = containerRef.current?.getBoundingClientRect();

  const renderContent = () => {
    if (display.breadcrumb) {
      if (!display.breadcrumb.length) {
        return (
          <span className="text-sm text-gray-400">{EMPTY_PLACEHOLDER}</span>
        );
      }
      return (
        <span className="text-xs leading-snug">
          {display.breadcrumb.map((segment, index) => (
            <span key={`${segment.value}-${index}`}>
              {index > 0 && (
                <span className="mx-1 text-gray-400" aria-hidden>
                  ›
                </span>
              )}
              <span
                className={cx(
                  segment.isDeepest
                    ? "font-medium text-gray-900"
                    : "text-gray-500",
                )}
              >
                {segment.value}
              </span>
            </span>
          ))}
        </span>
      );
    }

    if (!display.primary) {
      return <span className="text-sm text-gray-400">{EMPTY_PLACEHOLDER}</span>;
    }

    return (
      <div>
        <div className="text-sm font-medium text-gray-900">{display.primary}</div>
        {display.context && (
          <div className="mt-0.5 text-[11px] text-gray-500">{display.context}</div>
        )}
      </div>
    );
  };

  return (
    <>
      <td
        ref={cellRef}
        data-location-cell
        className={cx("px-3 py-2.5 align-top text-sm text-gray-600", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {renderContent()}
      </td>
      {tooltipAnchor && containerRect && tooltipRows.length > 0 && (
        <LocationHierarchyTooltip
          rows={tooltipRows}
          anchorRect={tooltipAnchor}
          containerRect={containerRect}
          onClose={closeTooltip}
        />
      )}
    </>
  );
};

export default LocationTableCell;
